import React, { useEffect, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { atom_tourState } from '@app/shared/state/tour.atom';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { atom_openedStopState } from '@app/shared/state/opened-stop.atom';
import { atom_showModalContent, atom_modalType } from '@app/shared/state/show-content-modal.atom';
import { atom_savedStopState } from '@app/shared/state/saved-stop-state.atom';
import { atom_tourStage } from '@app/shared/state/loading.atom';

/**
 * Consolidates all on-load state restoration:
 *   - Phase 1: set the initial active stop (savedStopState → last visited → first stop)
 *   - Phase 4: open the previously-open modal/BigStopPage once active stop matches saved
 *
 * Phase 2 (initial map flyTo) stays in NewMapContainer — it's tied to Leaflet's ready event.
 * Phase 3 (scroll-to-active card) is implicit via the existing active-stop effect in list components.
 */
export const StateRestorer = React.memo(function StateRestorer() {
	const tourState = useRecoilValue(atom_tourState);
	const savedStopState = useRecoilValue(atom_savedStopState);
	const state_activeStopState = useRecoilValue(atom_activeStopState);
	const setState_activeStopState = useSetRecoilState(atom_activeStopState);
	const setState_openedStopState = useSetRecoilState(atom_openedStopState);
	const setState_modalType = useSetRecoilState(atom_modalType);
	const setState_showModal = useSetRecoilState(atom_showModalContent);
	const tourStage = useRecoilValue(atom_tourStage);

	const hasSetInitialActiveStopRef = useRef(false);
	const hasRestoredSavedStateRef = useRef(false);

	// ── Phase 1: set the initial active stop (runs once when tour stops are loaded) ──
	useEffect(() => {
		if (hasSetInitialActiveStopRef.current) return;
		if (!tourState?.stops?.length) return;

		hasSetInitialActiveStopRef.current = true;

		// Priority 1: saved stop from previous session
		if (savedStopState?.stopId) {
			const savedStop = tourState.stops.find((s: any) => s.id === savedStopState.stopId);
			if (savedStop) {
				setState_activeStopState(savedStop);
				return;
			}
		}
		// Priority 2: last visited stop
		const cachedStop = Object.values(JSON.parse(localStorage.getItem('visitedStops') || '{}')) as string[];
		const latestStopId = cachedStop[cachedStop.length - 1];
		const latestStop = tourState.stops.find((s: any) => s.id === latestStopId);
		if (latestStop) {
			setState_activeStopState(latestStop);
			return;
		}
		// Priority 3: first stop
		setState_activeStopState(tourState.stops[0]);
	}, [tourState?.stops, savedStopState, setState_activeStopState]);

	// ── Phase 4: reopen the saved modal once active stop matches the saved id ──
	useEffect(() => {
		if (hasRestoredSavedStateRef.current) return;
		if (!savedStopState?.stopId) return;
		if (!state_activeStopState) return;
		if (tourStage !== 'map') return;
		if (state_activeStopState.id !== savedStopState.stopId) return;

		hasRestoredSavedStateRef.current = true;
		const t = setTimeout(() => {
			if (savedStopState.modalType === 'bigStop') {
				setState_openedStopState(state_activeStopState);
			} else if (savedStopState.modalType === 'stopsCards') {
				setState_showModal(true);
				setState_modalType(savedStopState.modalType);
			}
		}, 150);
		return () => clearTimeout(t);
	}, [savedStopState, state_activeStopState, tourStage, setState_openedStopState, setState_showModal, setState_modalType]);

	return null;
});
